import { DndContext } from "@dnd-kit/core";
import { useCustomers } from "@jugl-web/domain-resources/customers/hooks/useCustomers";
import { useTaskViewsContext } from "@jugl-web/domain-resources/tasks";
import { useTaskFiltering } from "@jugl-web/domain-resources/tasks/components/TaskFilteringProvider";
import { useTaskSorting } from "@jugl-web/domain-resources/tasks/components/TaskSortingProvider";
import { useTasks } from "@jugl-web/domain-resources/tasks/hooks/useTasks";
import { useTasksViewColumns } from "@jugl-web/domain-resources/tasks/hooks/useTasksViewColumns";
import {
  TaskViewType,
  useTasksViews,
} from "@jugl-web/domain-resources/tasks/hooks/useTasksViews";
import { useHeadlessUsersList } from "@jugl-web/domain-resources/users/hooks/useHeadlessUsersList";
import { PreviewTask } from "@jugl-web/rest-api/tasks";
import { useTranslations } from "@jugl-web/utils";
import { selectUserId } from "@web-src/features/auth/authSlice";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { FC, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { useSelector } from "react-redux";
import { MoreTasksLoading } from "../MoreTasksLoading";
import { TaskViewEmptyState } from "../TaskViewEmptyState";
import { TaskViewLoading } from "../TaskViewLoading";
import { TasksColumn } from "../TasksColumn";
import { useTasksPageContext } from "../TasksPageContext";
import { useTasksDnd } from "./hooks/useTasksDnd";

export const TaskKanbanView: FC = () => {
  const { taskListMode } = useTasksPageContext();

  const { searchQuery, filters, hasActiveFilter } = useTaskFiltering();
  const { sorting } = useTaskSorting();

  const { entityId } = useEntitySelectedProvider();
  const meId = useSelector(selectUserId);
  const { getViewById } = useTasksViews({ entityId });

  const { selectedView } = useTaskViewsContext();
  const { dndContextProps } = useTasksDnd();

  const { ref: moreTasksLoadingRef, inView } = useInView();

  const isAssigneeView = selectedView.type === TaskViewType.assignee;
  const isUserBasedView = isAssigneeView || taskListMode === "team";

  const {
    users,
    isLoading: areUsersLoading,
    loadMore: loadMoreUsers,
    reachedEnd: usersEndReached,
  } = useHeadlessUsersList({
    entityId,
    sortBy: "name",
    skipInitialLoading: !isUserBasedView,
    onlyReportees: taskListMode === "team",
  });

  const { customers, isLoading: areCustomersLoading } = useCustomers({
    entityId,
    skipLoading: selectedView.type !== TaskViewType.customer,
  });

  const { isLoading } = useTasks({ entityId, mode: taskListMode });

  const columns = useTasksViewColumns({
    entityId,
    meId: meId || "",
    mode: taskListMode,
    users,
    customers,
    searchQuery,
    filters,
    view:
      taskListMode === "personal"
        ? selectedView
        : getViewById(TaskViewType.reportee),
    sorting,
  });

  const { navigateToPage } = useNavigation();

  const { t } = useTranslations();

  const hasNoColumn = columns.length === 0;
  const hasAppliedAnyFiltering = searchQuery.length > 0 || hasActiveFilter;

  const shouldShowMoreTasksLoading =
    (isUserBasedView && !usersEndReached) ||
    (selectedView.id === TaskViewType.customer && areCustomersLoading);

  const handleTaskClick = (task: PreviewTask) => {
    navigateToPage("tasksDetails", { taskId: task.id });
  };

  useEffect(() => {
    if (isAssigneeView && inView && !areUsersLoading && !usersEndReached) {
      loadMoreUsers();
    }
  }, [inView, areUsersLoading, loadMoreUsers, usersEndReached, isAssigneeView]);

  if (isLoading) {
    return (
      <TaskViewLoading
        label={t({
          id: "tasks-page.loading-tasks",
          defaultMessage: "Loading Tasks...",
        })}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
      />
    );
  }

  if (hasNoColumn) {
    return (
      <TaskViewEmptyState
        subtitle={
          hasAppliedAnyFiltering
            ? t({
                id: "tasks-page.no-tasks-with-selected-filters",
                defaultMessage: "No tasks with selected filters",
              })
            : t({
                id: "tasks-page.no-tasks-in-selected-view",
                defaultMessage: "No tasks in selected view",
              })
        }
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
      />
    );
  }

  return (
    <DndContext {...dndContextProps}>
      <div
        key={`${taskListMode}-${selectedView.id}`}
        className="animate-fade-in flex h-full gap-6 px-8 pt-8 pb-3.5"
      >
        {columns.map((column) => (
          <TasksColumn
            key={column.id}
            column={column}
            onTaskClick={handleTaskClick}
          />
        ))}
        {shouldShowMoreTasksLoading && (
          <MoreTasksLoading ref={moreTasksLoadingRef} className="mx-2" />
        )}
        {/* Hack to prevent the last column from being cut off when the scrollbar is visible */}
        <div className="w-px shrink-0" />
      </div>
    </DndContext>
  );
};
